import {
  type FilterDraftValue,
  type UniversalBuiltInDrawerFieldConfig,
} from '../../types';

import { DateDrawerFieldControl } from './DateDrawerFieldControl';
import { ServerDrivenDrawerFieldControl } from './ServerDrivenDrawerFieldControl';
import { StaticSelectDrawerFieldControl } from './StaticSelectDrawerFieldControl';
import { TextDrawerFieldControl } from './TextDrawerFieldControl';
import { ToggleDrawerFieldControl } from './ToggleDrawerFieldControl';

type FiltersDrawerFieldControlProps = {
  readonly fieldConfig: UniversalBuiltInDrawerFieldConfig;
  readonly maxDate?: string;
  readonly minDate?: string;
  readonly onChange: (next?: FilterDraftValue) => void;
  readonly value?: FilterDraftValue;
};

function toStringValue(value?: FilterDraftValue) {
  return Array.isArray(value) || value === undefined || value === null
    ? ''
    : String(value);
}

function toStringArrayValue(value?: FilterDraftValue) {
  return Array.isArray(value) ? value.map((entry) => String(entry)) : [];
}

function toToggleValue(value?: FilterDraftValue) {
  return Array.isArray(value) || value === undefined || value === null
    ? null
    : String(value);
}

export function FiltersDrawerFieldControl({
  fieldConfig,
  maxDate,
  minDate,
  value,
  onChange,
}: FiltersDrawerFieldControlProps) {
  const testId = `universal-filters-drawer-control-${fieldConfig.field}`;

  switch (fieldConfig.input) {
    case 'multiSelect':
    case 'singleSelect': {
      return (
        <StaticSelectDrawerFieldControl
          fieldConfig={fieldConfig}
          testId={testId}
          value={
            fieldConfig.input === 'multiSelect'
              ? toStringArrayValue(value)
              : toStringValue(value)
          }
          onChange={onChange}
        />
      );
    }

    case 'booleanToggle':
    case 'toggleGroup': {
      return (
        <ToggleDrawerFieldControl
          fieldConfig={fieldConfig}
          testId={testId}
          value={toToggleValue(value)}
          onChange={onChange}
        />
      );
    }

    case 'text': {
      return (
        <TextDrawerFieldControl
          fieldConfig={fieldConfig}
          testId={testId}
          value={toStringValue(value)}
          onChange={onChange}
        />
      );
    }

    case 'date': {
      return (
        <DateDrawerFieldControl
          fieldConfig={fieldConfig}
          maxDate={maxDate}
          minDate={minDate}
          testId={testId}
          value={value}
          onChange={onChange}
        />
      );
    }

    case 'companies':
    case 'costCenters':
    case 'customFieldValues':
    case 'organizationalUnits':
    case 'sites':
    case 'suppliers':
    case 'users': {
      return (
        <ServerDrivenDrawerFieldControl
          fieldConfig={fieldConfig}
          value={toStringArrayValue(value)}
          onChange={onChange}
        />
      );
    }

    default: {
      return null;
    }
  }
}
